<template>
  <v-app id="inspire" style="background:#F9F9F9;">
    <vue-header-admin></vue-header-admin>
    <v-main>
      <v-container fluid class="px-6 py-6">
        <v-row align="center" justify="center">
          <v-col cols="12" md="12">
            <v-card class="card-shadow border-radius-xl mt-2" style="margin-bottom: 100px;">
              <v-card-text>
                <!-- Header -->
                <v-row align="center">
                  <v-col cols="12" md="6">
                    <h5 class="text-h4 font-weight-bold text-typo">
                      <v-icon left color="primary" class="material-icons-round">analytics</v-icon>
                      Vehicle Interests Tracking
                    </h5>
                    <p class="text-subtitle-2 text-grey mt-1">
                      Track user interactions with inventory vehicles
                    </p>
                  </v-col>
                  <v-col cols="12" md="6" class="text-right">
                    <rotate-loader class="mt-3" :loading="loading" color="#1D75F4"></rotate-loader>
                  </v-col>
                </v-row>

                <!-- Stats Cards -->
                <v-row class="mt-4">
                  <v-col cols="6" md="2">
                    <v-card class="stat-card" color="blue lighten-5" flat>
                      <v-card-text class="text-center py-4">
                        <div class="text-h4 font-weight-bold blue--text">{{ stats.total }}</div>
                        <div class="text-caption">Total Interactions</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-card class="stat-card" color="grey lighten-4" flat>
                      <v-card-text class="text-center py-4">
                        <div class="text-h4 font-weight-bold grey--text text--darken-2">{{ stats.clicked }}</div>
                        <div class="text-caption">Clicks</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="2">
                    <v-card class="stat-card" color="green lighten-5" flat>
                      <v-card-text class="text-center py-4">
                        <div class="text-h4 font-weight-bold green--text">{{ stats.interested }}</div>
                        <div class="text-caption">Interested</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-card class="stat-card" color="purple lighten-5" flat>
                      <v-card-text class="text-center py-4">
                        <div class="text-h4 font-weight-bold purple--text">{{ stats.unique_users }}</div>
                        <div class="text-caption">Unique Leads</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col cols="6" md="3">
                    <v-card class="stat-card" color="orange lighten-5" flat>
                      <v-card-text class="text-center py-4">
                        <div class="text-h4 font-weight-bold orange--text text--darken-2">{{ stats.unique_vehicles }}</div>
                        <div class="text-caption">Unique Vehicles</div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Filters -->
                <v-row class="mt-4">
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="search"
                      label="Search by name, email, phone, vehicle..."
                      clearable
                      dense
                      outlined
                      hide-details
                      @keyup.enter="loadInterests"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-select
                      v-model="filterType"
                      :items="typeOptions"
                      label="Interest Type"
                      dense
                      outlined
                      hide-details
                      clearable
                      @change="loadInterests"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="startDate" label="Start Date"  readonly v-bind="attrs" v-on="on" dense outlined hide-details clearable></v-text-field>
                      </template>
                      <v-date-picker v-model="startDate" @input="startDateMenu = false; loadInterests()"></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="endDate" label="End Date"  readonly v-bind="attrs" v-on="on" dense outlined hide-details clearable></v-text-field>
                      </template>
                      <v-date-picker v-model="endDate" @input="endDateMenu = false; loadInterests()"></v-date-picker>
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="2">
                    <v-btn color="blue" @click="loadInterests" dark block>
                      <v-icon class="material-icons-round" left>refresh</v-icon> Refresh
                    </v-btn >
                  </v-col>
                </v-row>

                <!-- Data Table -->
                <v-row class="mt-4">
                  <v-col cols="12">
                    <v-data-table
                      :headers="headers"
                      :items="interests"
                      :loading="loading"
                      :search="localSearch"
                      loading-text="Loading... Please wait"
                      class="elevation-1"
                      :footer-props="{
                        'items-per-page-options': [10, 25, 50, 100]
                      }"
                    >
                      <!-- Interest Type -->
                      <template v-slot:item.interest_type="{ item }">
                        <v-chip :color="getTypeColor(item.interest_type)" small dark>
                          <v-icon left small>{{ getTypeIcon(item.interest_type) }}</v-icon>
                          {{ ucfirst(item.interest_type) }}
                        </v-chip>
                      </template>

                      <!-- User Info -->
                      <template v-slot:item.user="{ item }">
                        <div v-if="item.user">
                          <div class="font-weight-bold">{{ item.user.name }}</div>
                          <div class="text-caption text-grey">{{ item.user.email }}</div>
                          <div class="text-caption text-grey">{{ item.user.phone }}</div>
                        </div>
                        <span v-else class="text-grey">Unknown User</span>
                      </template>

                      <!-- Vehicle Info -->
                      <template v-slot:item.vehicle="{ item }">
                        <div>
                          <div class="font-weight-bold">{{ item.vehicle.display }}</div>
                          <div class="text-caption text-grey">VIN: {{ item.vehicle.vin || 'N/A' }}</div>
                          <div class="text-caption green--text font-weight-bold">${{ formatPrice(item.vehicle.price) }}</div>
                        </div>
                      </template>

                      <!-- Dealership -->
                      <template v-slot:item.dealership="{ item }">
                        <div>
                          <div class="font-weight-medium">{{ item.dealership.name }}</div>
                          <v-chip v-if="item.dealership.notified" x-small color="green" dark class="mt-1">
                            <v-icon x-small left>mdi-check</v-icon> Notified
                          </v-chip>
                        </div>
                      </template>

                      <!-- Date -->
                      <template v-slot:item.created_at="{ item }">
                        <div>
                          <div>{{ formatDate(item.created_at) }}</div>
                          <div class="text-caption text-grey">{{ item.created_at_human }}</div>
                        </div>
                      </template>

                      <!-- Actions -->
                      <template v-slot:item.actions="{ item }">
                        <v-btn icon small @click="viewDetails(item)" color="primary">
                          <v-icon small class='material-icons-round'>visibility </v-icon>
                        </v-btn>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Detail Dialog -->
    <v-dialog v-model="detailDialog" max-width="600">
      <v-card v-if="selectedInterest">
        <v-card-text class="pt-2">
          <!-- User Section -->
          <div class="mb-4">
            <div class="text-overline mb-2">LEAD INFORMATION</div>
            <v-divider class="mb-2"></v-divider>
            <div v-if="selectedInterest.user">
              <p><strong>Name:</strong> {{ selectedInterest.user.name }}</p>
              <p><strong>Email:</strong> {{ selectedInterest.user.email }}</p>
              <p><strong>Phone:</strong> {{ selectedInterest.user.phone }}</p>
              <p><strong>Location:</strong> {{ selectedInterest.user.city }}, {{ selectedInterest.user.state }}</p>
            </div>
          </div>
          <!-- Vehicle Section -->
          <div class="mb-4">
            <div class="text-overline mb-2">VEHICLE INFORMATION</div>
            <v-divider class="mb-2"></v-divider>
            <p><strong>Vehicle:</strong> {{ selectedInterest.vehicle.display }}</p>
            <p><strong>VIN:</strong> {{ selectedInterest.vehicle.vin || 'N/A' }}</p>
            <p><strong>Price:</strong> ${{ formatPrice(selectedInterest.vehicle.price) }}</p>
          </div>
          <!-- Dealership Section -->
          <div class="mb-4">
            <div class="text-overline mb-2">DEALERSHIP</div>
            <v-divider class="mb-2"></v-divider>
            <p><strong>Name:</strong> {{ selectedInterest.dealership.name }}</p>
            <p><strong>Notified:</strong> {{ selectedInterest.dealership.notified ? 'Yes' : 'No' }}</p>
            <p v-if="selectedInterest.dealership.notified_at"><strong>Notified At:</strong> {{ selectedInterest.dealership.notified_at }}</p>
          </div>
          <!-- Meta Section -->
          <div>
            <div class="text-overline mb-2">TRACKING INFO</div>
            <v-divider class="mb-2"></v-divider>
            <p><strong>Type:</strong> <v-chip :color="getTypeColor(selectedInterest.interest_type)" small dark>{{ selectedInterest.interest_type }}</v-chip></p>
            <p><strong>Date:</strong> {{ formatDate(selectedInterest.created_at) }}</p>
            <p><strong>Domain:</strong> {{ selectedInterest.domain || 'N/A' }}</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
export default {
  name: 'VehicleInterests',
  data() {
    return {
      loading: false,
      interests: [],
      stats: { total: 0, viewed: 0, clicked: 0, interested: 0, unique_users: 0, unique_vehicles: 0 },
      search: '',
      localSearch: '',
      filterType: null,
      startDate: null,
      endDate: null,
      startDateMenu: false,
      endDateMenu: false,
      detailDialog: false,
      selectedInterest: null,
      typeOptions: [
        { text: 'All Types', value: null },
        { text: 'Viewed', value: 'viewed' },
        { text: 'Clicked', value: 'clicked' },
        { text: 'Interested', value: 'interested' }
      ],
      headers: [
        { text: 'Type', value: 'interest_type', width: '120px' },
        { text: 'Lead', value: 'user', sortable: false },
        { text: 'Vehicle', value: 'vehicle', sortable: false },
        { text: 'Dealership', value: 'dealership', sortable: false },
        { text: 'Date', value: 'created_at', width: '150px' },
        { text: 'Actions', value: 'actions', sortable: false, width: '80px' }
      ]
    };
  },
  mounted() {
    this.loadInterests();
  },
  methods: {
    async loadInterests() {
      this.loading = true;
      try {
        const params = {};
        if (this.search) params.search = this.search;
        if (this.filterType) params.type = this.filterType;
        if (this.startDate) params.start_date = this.startDate;
        if (this.endDate) params.end_date = this.endDate;

        const response = await window.axios.get('/api/vehicle-interest/all', { params });

        if (response.data.flag === 1) {
          this.interests = response.data.data;
          this.stats = response.data.stats;
        }
      } catch (error) {
        console.error('Error loading vehicle interests:', error);
        this.$toast?.error('Failed to load vehicle interests');
      } finally {
        this.loading = false;
      }
    },
    getTypeColor(type) {
      const colors = { viewed: 'grey', clicked: 'blue', interested: 'green' };
      return colors[type] || 'grey';
    },
    getTypeIcon(type) {
      const icons = { viewed: 'mdi-eye', clicked: 'mdi-cursor-default-click', interested: 'mdi-heart' };
      return icons[type] || 'mdi-help';
    },
    formatPrice(price) {
      if (!price) return '0';
      return parseFloat(price).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    viewDetails(item) {
      this.selectedInterest = item;
      this.detailDialog = true;
    },
    ucfirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  }
};
</script>

<style scoped>
.stat-card { border-radius: 12px; }
.stat-card .text-h4 { font-size: 1.75rem !important; }
</style>

